import { useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../../api/client';
import { DataTable } from '../../components/intelligence/DataTable';
import { TimeSeriesChart } from '../../components/intelligence/TimeSeriesChart';
import { SkeletonTable, SkeletonChart } from '../../components/intelligence/SkeletonLoader';
import { Button } from '../../components/ui';

interface QueryConfig {
  metric: string;
  groupBy: string;
  dateFrom: string;
  dateTo: string;
  filters: Array<{ field: string; op: string; value: string }>;
  limit: number;
}

const loading = signal(false);
const error = signal('');
const results = signal<any[]>([]);
const chartData = signal<Array<{ label: string; value: number }>>([]);
const savedQueries = signal<Array<{ name: string; config: QueryConfig }>>([]);

const METRICS = [
  { value: 'events', label: 'Eventos' },
  { value: 'sessions', label: 'Sessões' },
  { value: 'errors', label: 'Erros' },
  { value: 'latency', label: 'Latência' },
  { value: 'llm_requests', label: 'LLM Requests' },
  { value: 'llm_cost', label: 'LLM Custo' },
];

const GROUP_BY = [
  { value: '', label: 'Nenhum' },
  { value: 'date', label: 'Data' },
  { value: 'tool', label: 'Ferramenta' },
  { value: 'event_name', label: 'Evento' },
  { value: 'actor_id', label: 'Usuário' },
  { value: 'country', label: 'País' },
];

const FILTER_FIELDS = ['event_name', 'event_category', 'actor_id', 'tool', 'country'];
const FILTER_OPS = ['=', '!=', 'contains', 'starts_with'];

const selectCls = 'bg-gray-800 border border-gray-700 text-gray-200 rounded-lg px-3 py-2 text-sm focus:border-indigo-500 outline-none';
const inputCls = selectCls;

function QueryBuilderInner() {
  const [config, setConfig] = useState<QueryConfig>({
    metric: 'events',
    groupBy: 'date',
    dateFrom: new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10),
    dateTo: new Date().toISOString().slice(0, 10),
    filters: [],
    limit: 50,
  });

  const [queryName, setQueryName] = useState('');

  const updateConfig = (patch: Partial<QueryConfig>) => setConfig({ ...config, ...patch });

  const addFilter = () => {
    setConfig({ ...config, filters: [...config.filters, { field: 'event_name', op: '=', value: '' }] });
  };

  const removeFilter = (idx: number) => {
    setConfig({ ...config, filters: config.filters.filter((_, i) => i !== idx) });
  };

  const updateFilter = (idx: number, patch: Partial<{ field: string; op: string; value: string }>) => {
    const updated = config.filters.map((f, i) => i === idx ? { ...f, ...patch } : f);
    setConfig({ ...config, filters: updated });
  };

  async function runQuery() {
    loading.value = true;
    error.value = '';
    try {
      const params = new URLSearchParams();
      params.set('from', config.dateFrom);
      params.set('to', config.dateTo);
      params.set('limit', String(config.limit));
      config.filters.forEach((f) => {
        if (f.value) params.set(f.field, f.value);
      });

      if (config.groupBy === 'date') {
        const res = await api.get<any>(`/intelligence/aggregates?${params.toString()}`);
        const agg = res.aggregates || res;
        const daily = agg.daily_events || [];
        results.value = daily;
        chartData.value = daily.map((d: any) => ({ label: d.date, value: d.count || d.value || 0 }));
      } else {
        const res = await api.get<any>(`/intelligence/events?${params.toString()}`);
        results.value = res.events || [];
        chartData.value = [];
      }
    } catch (err: any) {
      error.value = err.message || 'Erro na query';
      results.value = [];
    } finally {
      loading.value = false;
    }
  }

  function saveQuery() {
    if (!queryName.trim()) return;
    const existing = savedQueries.value;
    savedQueries.value = [...existing, { name: queryName, config: { ...config } }];
    setQueryName('');
    try { localStorage.setItem('intel_saved_queries', JSON.stringify(savedQueries.value)); } catch (_) {}
  }

  function loadSavedQuery(cfg: QueryConfig) {
    setConfig(cfg);
  }

  return (
    <div>
 <h2 class="text-xl font-bold text-white mb-4">Query Builder</h2>

      {/* Guardrails */}
      <div class="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 rounded-lg p-3 mb-4 text-xs">
 Queries são limitadas a {config.limit} resultados. Máximo: 1000 rows. Use filtros para reduzir escopo.
      </div>

      {/* Builder */}
      <div class="bg-gray-900/50 rounded-xl border border-gray-800 p-4 mb-6 space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label class="text-xs text-gray-400 mb-1 block">Métrica</label>
            <select class={selectCls + ' w-full'} value={config.metric} onChange={(e) => updateConfig({ metric: (e.target as HTMLSelectElement).value })}>
              {METRICS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label class="text-xs text-gray-400 mb-1 block">Agrupar por</label>
            <select class={selectCls + ' w-full'} value={config.groupBy} onChange={(e) => updateConfig({ groupBy: (e.target as HTMLSelectElement).value })}>
              {GROUP_BY.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>
          <div>
            <label class="text-xs text-gray-400 mb-1 block">De</label>
            <input type="date" class={inputCls + ' w-full'} value={config.dateFrom} onChange={(e) => updateConfig({ dateFrom: (e.target as HTMLInputElement).value })} />
          </div>
          <div>
            <label class="text-xs text-gray-400 mb-1 block">Até</label>
            <input type="date" class={inputCls + ' w-full'} value={config.dateTo} onChange={(e) => updateConfig({ dateTo: (e.target as HTMLInputElement).value })} />
          </div>
        </div>

        {/* Dynamic filters */}
        <div>
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs text-gray-400">Filtros</span>
            <Button variant="ghost" class="text-xs text-indigo-400 hover:text-indigo-300" onClick={addFilter}>+ Adicionar filtro</Button>
          </div>
          {config.filters.map((f, idx) => (
            <div key={idx} class="flex gap-2 mb-2 items-center">
              <select class={selectCls} value={f.field} onChange={(e) => updateFilter(idx, { field: (e.target as HTMLSelectElement).value })}>
                {FILTER_FIELDS.map((ff) => <option key={ff} value={ff}>{ff}</option>)}
              </select>
              <select class={selectCls} value={f.op} onChange={(e) => updateFilter(idx, { op: (e.target as HTMLSelectElement).value })}>
                {FILTER_OPS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <input class={inputCls + ' flex-1'} value={f.value} placeholder="Valor" onInput={(e) => updateFilter(idx, { value: (e.target as HTMLInputElement).value })} />
              <Button variant="ghost" class="text-red-400 text-sm hover:text-red-300" onClick={() => removeFilter(idx)}></Button>
            </div>
          ))}
        </div>

        <div class="flex gap-3">
          <Button variant="ghost"
            class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
            onClick={runQuery}
          >
            Executar
          </Button>
          <div class="flex gap-2 items-center">
            <input class={inputCls} placeholder="Nome da query" value={queryName} onInput={(e) => setQueryName((e.target as HTMLInputElement).value)} />
            <Button variant="ghost"
              class="px-3 py-2 bg-gray-800 text-gray-300 text-sm rounded-lg hover:bg-gray-700 transition-colors"
              onClick={saveQuery}
            >
 Salvar
            </Button>
          </div>
        </div>
      </div>

      {/* Saved queries */}
      {savedQueries.value.length > 0 && (
        <div class="mb-6">
          <h3 class="text-sm font-medium text-gray-300 mb-2">Queries Salvas</h3>
          <div class="flex flex-wrap gap-2">
            {savedQueries.value.map((q, i) => (
              <Button variant="ghost"
                key={i}
                class="px-3 py-1.5 bg-gray-800 text-gray-300 text-xs rounded-lg hover:bg-gray-700 transition-colors"
                onClick={() => loadSavedQuery(q.config)}
              >
 {q.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {error.value && (
        <div class="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 mb-4 text-sm">{error.value}</div>
      )}

      {/* Results */}
      {loading.value ? (
        <>
          <SkeletonChart />
          <div class="mt-4"><SkeletonTable /></div>
        </>
      ) : results.value.length > 0 ? (
        <>
          {chartData.value.length > 0 && (
            <div class="mb-6 [&>div]:!bg-gray-800 [&>div]:!border-gray-700">
              <TimeSeriesChart title="Resultados" data={chartData.value} color="#6366f1" />
            </div>
          )}
          <div class="[&>div]:!bg-gray-800 [&>div]:!border-gray-700 [&_th]:!bg-gray-800 [&_th]:!text-gray-400 [&_td]:!text-gray-300">
            <DataTable
              columns={Object.keys(results.value[0] || {}).map((k) => ({ key: k, label: k }))}
              data={results.value}
              pageSize={20}
              emptyMessage="Sem resultados"
            />
          </div>
        </>
      ) : null}
    </div>
  );
}

export function QueryBuilderPanel() {
  return <QueryBuilderInner />;
}
